import React, { useState, useEffect, useCallback } from 'react';
import { getAuthToken } from '../../api/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { FileSearch, RefreshCw, ExternalLink, Check, Undo2, ChevronDown, ChevronRight, Cloud, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const RESULTADOS = {
  cuadre_fuerte: { label: 'Cuadre fuerte', clase: 'bg-green-100 text-green-800' },
  auto_vinculada: { label: 'Vinculada auto', clase: 'bg-green-600 text-white' },
  auto_vinculada_diferido: { label: 'Vinculada auto (diferido)', clase: 'bg-emerald-600 text-white' },
  vinculada_manual: { label: 'Vinculada manual', clase: 'bg-blue-100 text-blue-800' },
  a_revision: { label: 'A revisión', clase: 'bg-amber-100 text-amber-800' },
  sin_pago_detectado: { label: 'Sin pago detectado', clase: 'bg-orange-100 text-orange-800' },
  duplicada: { label: 'Duplicada', clase: 'bg-gray-200 text-gray-700' },
  error: { label: 'Error', clase: 'bg-red-100 text-red-800' },
};

const fmtImporte = (v) =>
  v === null || v === undefined ? '-' : Number(v).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });

/**
 * Registro del procesamiento automático de facturas (vigilante de Drive/Soportadas).
 * Fase 1: modo lectura — muestra lo que la IA extrae y lo que se vincularía,
 * sin ejecutar acciones. Sirve para validar antes de activar el automático.
 */
const RESULTADOS_COBRO = {
  auto_vinculado: { label: 'Vinculado auto', clase: 'bg-green-600 text-white' },
  cuadre_detectado: { label: 'Cuadre detectado', clase: 'bg-green-100 text-green-800' },
  empate_revision: { label: 'Empate, revisar', clase: 'bg-amber-100 text-amber-800' },
  error: { label: 'Error', clase: 'bg-red-100 text-red-800' },
};

const RegistroAutomatizacion = () => {
  const token = getAuthToken();
  const [registros, setRegistros] = useState([]);
  const [registrosCobros, setRegistrosCobros] = useState([]);
  const [ultimaEjecucion, setUltimaEjecucion] = useState(null);
  const [pendientesVB, setPendientesVB] = useState(0);
  const [filtro, setFiltro] = useState('');
  const [verRevisadas, setVerRevisadas] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [ejecutando, setEjecutando] = useState(false);
  const [expandido, setExpandido] = useState(false);  // registro plegado por defecto
  const [subiendoA3docId, setSubiendoA3docId] = useState(null);

  const cargar = useCallback(async (resultado = '', incluirRevisadas = false) => {
    setCargando(true);
    try {
      const url = `${API_URL}/api/finanzas/automatizacion/registro?limit=100${resultado ? `&resultado=${resultado}` : ''}${incluirRevisadas ? '&incluir_revisadas=true' : ''}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setRegistros(data.registros || []);
        setUltimaEjecucion(data.ultima_ejecucion);
        setPendientesVB(data.pendientes_visto_bueno || 0);
      }
      const resCobros = await fetch(`${API_URL}/api/finanzas/automatizacion/registro-cobros?limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resCobros.ok) {
        const dataCobros = await resCobros.json();
        setRegistrosCobros(dataCobros.registros || []);
      }
    } catch (e) {
      console.error('Error cargando registro de automatizacion:', e);
    } finally {
      setCargando(false);
    }
  }, [token]);

  useEffect(() => { cargar(filtro, verRevisadas); }, [cargar, filtro, verRevisadas]);

  // Visto bueno de una entrada auto-vinculada (revisado=false para deshacer)
  const darVistoBueno = async (id, revisado = true) => {
    try {
      const res = await fetch(
        `${API_URL}/api/finanzas/automatizacion/registro/${id}/visto-bueno?revisado=${revisado}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        cargar(filtro, verRevisadas);
      } else {
        alert('No se pudo actualizar el visto bueno');
      }
    } catch (e) {
      alert('Error dando el visto bueno');
    }
  };

  // Sube a a3doc una factura pendiente de vincular (requiere ficha OCR)
  const subirA3doc = async (facturaDriveId) => {
    setSubiendoA3docId(facturaDriveId);
    try {
      const res = await fetch(`${API_URL}/api/a3doc/subir-factura-pendiente/${facturaDriveId}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        cargar(filtro, verRevisadas);
      } else {
        alert('No se pudo subir a a3doc: ' + (data.detail || 'error desconocido'));
      }
    } catch (e) {
      alert('Error subiendo a a3doc');
    } finally {
      setSubiendoA3docId(null);
    }
  };

  // Visto bueno a todas las auto-vinculadas pendientes
  const vistoBuenoTodas = async () => {
    if (!window.confirm(`¿Dar el visto bueno a las ${pendientesVB} facturas auto-vinculadas pendientes?\n\nDesaparecerán de la lista (quedan en el histórico, visibles con "Ver revisadas").`)) return;
    try {
      const res = await fetch(`${API_URL}/api/finanzas/automatizacion/registro/visto-bueno-masivo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (res.ok) {
        cargar(filtro, verRevisadas);
      } else {
        alert(data.detail || 'Error al dar el visto bueno masivo');
      }
    } catch (e) {
      alert('Error al dar el visto bueno masivo');
    }
  };

  const ejecutarAhora = async () => {
    setEjecutando(true);
    try {
      const res = await fetch(`${API_URL}/api/finanzas/automatizacion/ejecutar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && !data.error) {
        alert(`Pasada completada\n\nArchivos nuevos: ${data.nuevos}\nCuadres fuertes: ${data.cuadres_fuertes}\nA revisión: ${data.a_revision}\nDuplicadas: ${data.duplicadas}\nErrores: ${data.errores}`);
        cargar(filtro);
      } else {
        alert(data.error || data.detail || 'Error ejecutando el vigilante');
      }
    } catch (e) {
      alert('Error ejecutando el vigilante');
    } finally {
      setEjecutando(false);
    }
  };

  return (
    <Card className="mb-6">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <button
            type="button"
            onClick={() => setExpandido((v) => !v)}
            className="flex items-center gap-2 text-left"
            title={expandido ? 'Ocultar registro' : 'Ver registro'}
          >
            {expandido ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
            <CardTitle className="text-lg flex items-center gap-2">
              <FileSearch className="w-5 h-5 text-duetto-primary" />
              Registro de procesamiento automático
            </CardTitle>
          </button>
          <p className="text-sm text-gray-500 mt-1">
            Facturas nuevas de Drive/Soportadas leídas con IA; los cuadres fuertes se vinculan, mueven y suben a a3doc solos
          </p>
          {ultimaEjecucion && (
            <p className="text-[11px] text-gray-400 mt-1">
              Última pasada: {new Date(ultimaEjecucion).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })}
            </p>
          )}
          {!expandido && (registros.length > 0 || registrosCobros.length > 0) && (
            <p className="text-[11px] text-gray-400 mt-1">
              {registros.length} de facturas · {registrosCobros.length} de cobros
              {pendientesVB > 0 ? ` · ${pendientesVB} pendientes de visto bueno` : ''} · pulsa el título para desplegar
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <label className="flex items-center gap-1 text-xs text-gray-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={verRevisadas}
              onChange={(e) => setVerRevisadas(e.target.checked)}
            />
            Ver revisadas
          </label>
          <select
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            className="border rounded-md px-2 py-1.5 text-sm"
          >
            <option value="">Todos</option>
            <option value="auto_vinculada">Vinculadas auto</option>
            <option value="auto_vinculada_diferido">Vinculadas auto (diferido)</option>
            <option value="cuadre_fuerte">Cuadres fuertes</option>
            <option value="a_revision">A revisión</option>
            <option value="sin_pago_detectado">Sin pago detectado</option>
            <option value="duplicada">Duplicadas</option>
            <option value="error">Errores</option>
          </select>
          {pendientesVB > 0 && (
            <Button variant="outline" onClick={vistoBuenoTodas} title="Marcar como revisadas todas las auto-vinculadas pendientes">
              <Check className="w-4 h-4 mr-2 text-green-600" />
              Visto bueno a todas ({pendientesVB})
            </Button>
          )}
          <Button variant="outline" onClick={ejecutarAhora} disabled={ejecutando}>
            <RefreshCw className={`w-4 h-4 mr-2 ${ejecutando ? 'animate-spin' : ''}`} />
            Procesar ahora
          </Button>
        </div>
      </CardHeader>
      {expandido && (
      <CardContent>
        {cargando ? (
          <p className="text-sm text-gray-500 py-4">Cargando...</p>
        ) : registros.length === 0 ? (
          <p className="text-sm text-gray-500 py-4">
            Sin registros todavía. El vigilante pasa cada día a las 08:00, o pulsa "Procesar ahora".
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b">
                  <th className="py-2 pr-3">Archivo</th>
                  <th className="py-2 pr-3">Proveedor</th>
                  <th className="py-2 pr-3">Nº factura</th>
                  <th className="py-2 pr-3">Fecha</th>
                  <th className="py-2 pr-3 text-right">Total</th>
                  <th className="py-2 pr-3">Resultado</th>
                  <th className="py-2 pr-3">Cargo candidato / motivo</th>
                  <th className="py-2 pr-3 text-center">a3doc</th>
                  <th className="py-2 pr-3 text-center">Visto bueno</th>
                </tr>
              </thead>
              <tbody>
                {registros.map((r) => {
                  const datos = r.datos_extraidos || {};
                  const res = RESULTADOS[r.resultado] || { label: r.resultado, clase: 'bg-gray-100 text-gray-700' };
                  return (
                    <tr key={r.id} className="border-b last:border-0 align-top">
                      <td className="py-2 pr-3 max-w-[180px]">
                        <a
                          href={r.drive_link}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 hover:underline inline-flex items-center gap-1"
                          title={r.archivo_nombre}
                        >
                          <span className="truncate max-w-[150px] inline-block">{r.archivo_nombre}</span>
                          <ExternalLink className="w-3 h-3 flex-shrink-0" />
                        </a>
                      </td>
                      <td className="py-2 pr-3">{datos.proveedor_nombre || '-'}</td>
                      <td className="py-2 pr-3">{datos.numero_factura || '-'}</td>
                      <td className="py-2 pr-3 whitespace-nowrap">{datos.fecha_factura || '-'}</td>
                      <td className="py-2 pr-3 text-right whitespace-nowrap">{fmtImporte(datos.importe_total)}</td>
                      <td className="py-2 pr-3">
                        <Badge className={`${res.clase} font-normal`}>{res.label}</Badge>
                      </td>
                      <td className="py-2 pr-3 text-xs text-gray-600 max-w-[260px]">
                        {r.cargo_candidato ? (
                          <span>
                            {r.cargo_candidato.fecha} · {fmtImporte(r.cargo_candidato.importe)} · {(r.cargo_candidato.concepto || '').substring(0, 50)}
                            <br />
                          </span>
                        ) : null}
                        <span className="text-gray-400">{r.cuadre_motivo || r.error || ''}</span>
                        {r.acciones?.vinculada && (
                          <span className="block text-green-700 mt-0.5">
                            {[
                              'vinculada',
                              r.acciones.movida_drive ? `movida a ${r.acciones.carpeta_destino || 'Vinculadas'}` : null,
                              r.acciones.subida_a3doc ? 'subida a a3doc' : null,
                            ].filter(Boolean).join(' · ')}
                          </span>
                        )}
                        {(r.errores_accion || []).length > 0 && (
                          <span className="block text-red-600 mt-0.5">{r.errores_accion.join(' · ').substring(0, 80)}</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-center whitespace-nowrap">
                        {r.a3doc_document_id ? (
                          <span
                            className="inline-flex items-center gap-1 text-xs text-green-700"
                            title={`Subida a a3doc${r.a3doc_subido_en ? ' · ' + new Date(r.a3doc_subido_en).toLocaleDateString('es-ES') : ''}`}
                          >
                            <CheckCircle2 className="w-3 h-3" /> a3doc
                          </span>
                        ) : r.factura_drive_id && r.resultado !== 'duplicada' && r.resultado !== 'error' ? (
                          <button
                            onClick={() => subirA3doc(r.factura_drive_id)}
                            disabled={subiendoA3docId === r.factura_drive_id}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 text-xs font-medium disabled:opacity-60"
                            title="Enviar a a3doc; la factura sigue pendiente de vincular"
                          >
                            <Cloud className="w-3.5 h-3.5" /> {subiendoA3docId === r.factura_drive_id ? 'Subiendo...' : 'a3doc'}
                          </button>
                        ) : (
                          <span className="text-gray-300">–</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-center whitespace-nowrap">
                        {['auto_vinculada', 'auto_vinculada_diferido'].includes(r.resultado) ? (
                          r.revisado ? (
                            <button
                              onClick={() => darVistoBueno(r.id, false)}
                              className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
                              title={`Visto bueno${r.revisado_por ? ' por ' + r.revisado_por : ''}${r.revisado_en ? ' · ' + new Date(r.revisado_en).toLocaleDateString('es-ES') : ''}. Pulsa para deshacer.`}
                            >
                              <Check className="w-3.5 h-3.5 text-green-600" />
                              <Undo2 className="w-3 h-3" />
                            </button>
                          ) : (
                            <button
                              onClick={() => darVistoBueno(r.id, true)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-green-50 text-green-700 hover:bg-green-100 text-xs font-medium"
                              title="Dar el visto bueno: se archiva y desaparece de la lista"
                            >
                              <Check className="w-3.5 h-3.5" /> OK
                            </button>
                          )
                        ) : (
                          <span className="text-gray-300">–</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {registrosCobros.length > 0 && (
          <div className="mt-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Cobros (abonos bancarios → presupuestos)</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b">
                    <th className="py-2 pr-3">Fecha abono</th>
                    <th className="py-2 pr-3">Concepto del banco</th>
                    <th className="py-2 pr-3 text-right">Importe</th>
                    <th className="py-2 pr-3">Resultado</th>
                    <th className="py-2 pr-3">Presupuesto / motivo</th>
                  </tr>
                </thead>
                <tbody>
                  {registrosCobros.map((r) => {
                    const res = RESULTADOS_COBRO[r.resultado] || { label: r.resultado, clase: 'bg-gray-100 text-gray-700' };
                    return (
                      <tr key={r.id || r.abono_id} className="border-b last:border-0 align-top">
                        <td className="py-2 pr-3 whitespace-nowrap">{r.abono?.fecha || '-'}</td>
                        <td className="py-2 pr-3 max-w-[260px]">
                          <span className="text-xs">{(r.abono?.concepto || '').substring(0, 70)}</span>
                        </td>
                        <td className="py-2 pr-3 text-right whitespace-nowrap">{fmtImporte(r.abono?.importe)}</td>
                        <td className="py-2 pr-3">
                          <Badge className={`${res.clase} font-normal`}>{res.label}</Badge>
                        </td>
                        <td className="py-2 pr-3 text-xs text-gray-600 max-w-[260px]">
                          {r.presupuesto ? (
                            <span>
                              #{r.presupuesto.numero} · {r.presupuesto.cliente}
                              {r.factura_numero ? ` · ${r.factura_numero}` : ''}
                              <br />
                            </span>
                          ) : null}
                          <span className="text-gray-400">{r.motivo || r.error || ''}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
      )}
    </Card>
  );
};

export default RegistroAutomatizacion;
